
import Layout from '../components/Layout';
import { Button } from '@/components/ui/button';
import { Check, Crown, ShieldCheck, Star, Zap, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

interface PricingTier {
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  buttonText: string;
  buttonAction?: "checkout" | "discord";
  highlighted?: boolean;
  crownType?: 'basic' | 'premium';
  premiumFeatures?: number;
}

const Premium = () => {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  
  const pricingTiers: PricingTier[] = [
    {
      name: "Basic",
      price: "$0",
      period: "forever",
      description: "Essential features for small servers",
      features: [
        "Basic moderation commands",
        "Limited AI test period",
        "Server management tools",
        "Basic security features",
        "Standard support",
      ],
      buttonText: "Add to Discord",
      buttonAction: "discord",
      premiumFeatures: 0
    },
    {
      name: "Plus",
      price: billingPeriod === 'monthly' ? "$4.99" : "$49.99",
      period: billingPeriod === 'monthly' ? "per month" : "per year",
      description: "Enhanced features for growing communities",
      features: [
        "All Basic features",
        "Advanced moderation tools",
        "Enhanced AI interactions (50/day)",
        "Customizable responses",
        "Advanced security features",
        "Priority support",
      ],
      buttonText: "Go Plus",
      buttonAction: "checkout",
      highlighted: true,
      crownType: 'basic',
      premiumFeatures: 5
    },
    {
      name: "Pro",
      price: billingPeriod === 'monthly' ? "$9.99" : "$99.99",
      period: billingPeriod === 'monthly' ? "per month" : "per year",
      description: "Premium features for large servers",
      features: [
        "All Plus features",
        "Unlimited AI interactions",
        "Enhanced security toolkit",
        "Dedicated support team",
        "Advanced analytics",
        "White-label options",
        "SLA guarantees",
      ],
      buttonText: "Go Pro",
      buttonAction: "checkout",
      crownType: 'premium',
      premiumFeatures: 11
    },
  ];

  return (
    <Layout>
      <div className="py-24 bg-gradient-to-b from-white to-pink-50 dark:from-gray-900 dark:to-gray-800">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 dark:text-white">
              Upgrade to <span className="text-fembot-pink">Premium</span>
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Choose the perfect plan for your Discord server and unlock powerful features
            </p>
            <p className="text-sm text-fembot-pink mt-3 italic">
              Some features are still in development and may not be available at this point in time
            </p>
          </div>

          <div className="flex justify-center mb-10">
            <div className="bg-white dark:bg-gray-800 rounded-full p-1 shadow-md inline-flex">
              <button
                className={`px-6 py-2 rounded-full transition-all ${
                  billingPeriod === 'monthly'
                    ? 'bg-fembot-pink text-white'
                    : 'text-gray-600 dark:text-gray-300 hover:text-fembot-pink'
                }`}
                onClick={() => setBillingPeriod('monthly')}
              >
                Monthly
              </button>
              <button
                className={`px-6 py-2 rounded-full transition-all ${
                  billingPeriod === 'yearly'
                    ? 'bg-fembot-pink text-white'
                    : 'text-gray-600 dark:text-gray-300 hover:text-fembot-pink'
                }`}
                onClick={() => setBillingPeriod('yearly')}
              >
                Yearly <span className="text-xs font-medium">Save 16%</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingTiers.map((tier, index) => (
              <div
                key={index}
                className={`rounded-xl overflow-hidden shadow-lg transition-all hover:shadow-xl ${
                  tier.highlighted
                    ? 'ring-2 ring-fembot-pink transform scale-105 bg-white dark:bg-gray-800'
                    : tier.crownType === 'premium'
                    ? 'bg-gradient-to-b from-gray-900 to-gray-800 text-white'
                    : 'bg-white dark:bg-gray-800'
                }`}
              >
                {tier.highlighted ? (
                  <div className="bg-fembot-pink text-white text-center py-2 font-medium">
                    Most Popular
                  </div>
                ) : tier.crownType === 'premium' ? (
                  <div className="bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-600 text-white text-center py-3 font-medium flex items-center justify-center gap-2">
                    <Sparkles className="h-5 w-5" />
                    <span className="font-bold">PROFESSIONAL PLAN</span>
                    <Sparkles className="h-5 w-5" />
                  </div>
                ) : null}
                
                <div className={`p-8 ${tier.crownType === 'premium' ? 'text-white' : ''}`}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className={`text-2xl font-bold ${tier.crownType === 'premium' ? 'text-white' : 'dark:text-white'}`}>{tier.name}</h3>
                    {tier.crownType === 'basic' && (
                      <div className="bg-yellow-100 dark:bg-yellow-900/30 p-2 rounded-full">
                        <Crown className="text-yellow-500 h-6 w-6" />
                      </div>
                    )}
                    {tier.crownType === 'premium' && (
                      <div className="bg-gradient-to-r from-yellow-300 to-amber-500 p-2.5 rounded-full shadow-lg">
                        <Crown className="h-7 w-7 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="flex items-baseline mb-6">
                    <span className={`text-4xl font-bold ${tier.crownType === 'premium' ? 'text-white' : 'dark:text-white'}`}>{tier.price}</span>
                    <span className={`${tier.crownType === 'premium' ? 'text-gray-300' : 'text-gray-500 dark:text-gray-400'} ml-2`}>{tier.period}</span>
                  </div>
                  <p className={`${tier.crownType === 'premium' ? 'text-gray-300' : 'text-gray-600 dark:text-gray-400'} mb-8`}>{tier.description}</p>
                  
                  {tier.premiumFeatures !== undefined && tier.premiumFeatures > 0 && (
                    <div className={`mb-6 ${tier.crownType === 'premium' ? 'bg-gray-800' : 'bg-gray-100 dark:bg-gray-700'} rounded-lg p-3`}>
                      <p className={`text-sm ${tier.crownType === 'premium' ? 'text-yellow-400' : 'text-fembot-pink'} font-medium flex items-center gap-1`}>
                        <Star className="h-4 w-4" /> 
                        Includes {tier.premiumFeatures} premium feature{tier.premiumFeatures > 1 ? 's' : ''}
                      </p>
                    </div>
                  )}
                  
                  {tier.buttonAction === "checkout" ? (
                    //<Link to={`/checkout?plan=${tier.name.toLowerCase()}&period=${billingPeriod}`}>
                    <Link to={`/info`}>
                      <Button
                        className={`w-full mb-8 ${
                          tier.highlighted
                            ? 'bg-fembot-pink hover:bg-fembot-dark-pink text-white'
                            : tier.crownType === 'premium'
                            ? 'bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600 text-black font-bold shadow-lg'
                            : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-white'
                        }`}
                      >
                        {tier.buttonText}
                      </Button>
                    </Link>
                  ) : (
                    <Button
                      className="w-full mb-8 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-white"
                     // onClick={() => window.open("https://discord.com/oauth2/authorize?client_id=1324844843670835240", "_blank")}
                     onClick={() => window.location.href = "/info"}
                    >
                      {tier.buttonText}
                    </Button>
                  )}
                  
                  <ul className="space-y-4">
                    {tier.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        {tier.crownType === 'premium' ? (
                          <div className="bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full p-1 mr-3 mt-0.5">
                            <Check className="h-3 w-3 text-black" />
                          </div>
                        ) : tier.highlighted ? (
                          <div className="bg-fembot-pink rounded-full p-1 mr-3 mt-0.5">
                            <Check className="h-3 w-3 text-white" />
                          </div>
                        ) : (
                          <div className="bg-gray-200 dark:bg-gray-700 rounded-full p-1 mr-3 mt-0.5">
                            <Check className="h-3 w-3 text-gray-700 dark:text-gray-300" />
                          </div>
                        )}
                        <span className={`${tier.crownType === 'premium' ? 'text-gray-300' : 'text-gray-600 dark:text-gray-300'}`}>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              All plans include core moderation features, server management tools, and access to our support community.
              Need help choosing? <Link to="/support" className="text-fembot-pink hover:underline">Contact us</Link>.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Premium;
